export const DEFAULT_REALTIME_SNAPSHOT = {
  ownershipState: "passive",
  isOwner: false,
  connectionState: "disconnected",
  clientId: null,
  lastMessage: null,
  friends: null,
  lobby: null,
  matchmaking: null,
  matchFound: null,
  matchReady: null,
  mapVote: null,
  notifications: [],
};

function deepClonePlain(value) {
  if (value === null || typeof value !== "object") return value;
  if (typeof structuredClone === "function") {
    try {
      return structuredClone(value);
    } catch {
      /* non-cloneable; fall through */
    }
  }
  if (Array.isArray(value)) {
    return value.map(deepClonePlain);
  }
  const out = {};
  for (const k of Object.keys(value)) {
    out[k] = deepClonePlain(value[k]);
  }
  return out;
}

function cloneRealtimeSnapshot(source) {
  const s = source;
  const out = { ...s };
  if (out.friends != null && typeof out.friends === "object") {
    out.friends = deepClonePlain(out.friends);
  }
  if (out.lobby != null && typeof out.lobby === "object") {
    out.lobby = deepClonePlain(out.lobby);
  }
  if (Array.isArray(out.notifications)) {
    out.notifications = out.notifications.map(deepClonePlain);
  }
  if (out.matchFound != null && typeof out.matchFound === "object") {
    out.matchFound = deepClonePlain(out.matchFound);
  }
  if (out.mapVote != null && typeof out.mapVote === "object") {
    out.mapVote = deepClonePlain(out.mapVote);
  }
  if (out.matchReady != null && typeof out.matchReady === "object") {
    out.matchReady = { ...out.matchReady };
  }
  if (out.matchmaking != null && typeof out.matchmaking === "object") {
    out.matchmaking = { ...out.matchmaking };
  }
  if (out.lastMessage != null && typeof out.lastMessage === "object") {
    out.lastMessage = deepClonePlain(out.lastMessage);
  }
  return out;
}

export function reduceRealtimeSnapshot(snapshot, event) {
  if (!event) return snapshot;

  if (event.type === "snapshot") {
    return {
      ...snapshot,
      ...event.snapshot,
    };
  }

  if (event.type === "connection-state") {
    return {
      ...snapshot,
      ownershipState: Object.prototype.hasOwnProperty.call(event, "ownershipState")
        ? event.ownershipState
        : snapshot.ownershipState,
      isOwner: Object.prototype.hasOwnProperty.call(event, "isOwner")
        ? Boolean(event.isOwner)
        : snapshot.isOwner,
      connectionState: Object.prototype.hasOwnProperty.call(event, "connectionState")
        ? event.connectionState
        : snapshot.connectionState,
      clientId: Object.prototype.hasOwnProperty.call(event, "clientId")
        ? event.clientId
        : snapshot.clientId,
    };
  }

  switch (event.messageId) {
    case "handshake-completed":
      return {
        ...snapshot,
        lastMessage: event,
      };
    case "match-found-notification":
      return {
        ...snapshot,
        lastMessage: event,
        matchFound: {
          matchId: event.matchId,
          timeoutSeconds: event.timeoutSeconds,
          expiresAt: event.expiresAt ?? null,
          map: event.map || null,
          mapVotePending: Boolean(event.mapVotePending),
          location: event.location || null,
          totalPlayers: event.totalPlayers ?? null,
          acceptedCount: Number(event.acceptedCount ?? 0),
          allAccepted: Boolean(event.allAccepted),
          hasAccepted: Boolean(event.hasAccepted),
          slots: deepClonePlain(event.slots || { team1: [], team2: [] }),
          teams: deepClonePlain(event.teams || null),
        },
      };
    case "match-acceptance-update":
      return {
        ...snapshot,
        lastMessage: event,
        matchFound:
          snapshot.matchFound?.matchId === event.matchId
            ? {
                ...snapshot.matchFound,
                expiresAt: event.expiresAt ?? snapshot.matchFound.expiresAt ?? null,
                totalPlayers:
                  event.totalPlayers ?? snapshot.matchFound.totalPlayers ?? null,
                acceptedCount: Number(
                  event.acceptedCount ?? snapshot.matchFound.acceptedCount ?? 0
                ),
                allAccepted: Boolean(event.allAccepted),
                hasAccepted: Boolean(event.hasAccepted),
                slots: deepClonePlain(
                  event.slots || snapshot.matchFound.slots || { team1: [], team2: [] }
                ),
                mapVotePending:
                  event.mapVotePending != null
                    ? Boolean(event.mapVotePending)
                    : snapshot.matchFound.mapVotePending,
                map:
                  event.map !== undefined
                    ? event.map || null
                    : snapshot.matchFound.map,
                teams:
                  event.teams != null
                    ? deepClonePlain(event.teams)
                    : snapshot.matchFound.teams || null,
              }
            : snapshot.matchFound,
      };
    case "match-acceptance-cancelled":
      return {
        ...snapshot,
        lastMessage: event,
        matchFound:
          snapshot.matchFound?.matchId === event.matchId ? null : snapshot.matchFound,
        // A cancelled match must not keep a vote snapshot for a later owner tab.
        mapVote:
          snapshot.mapVote?.matchId === event.matchId ? null : snapshot.mapVote,
      };
    case "map-vote-started":
      return {
        ...snapshot,
        lastMessage: event,
        mapVote: {
          matchId: event.matchId,
          candidates: Array.isArray(event.candidates)
            ? event.candidates.slice()
            : [],
          blockedMaps: deepClonePlain(event.blockedMaps || []),
          expiresAt: event.expiresAt,
          durationSeconds: event.durationSeconds,
          voters: deepClonePlain(event.voters || []),
          votes: deepClonePlain(event.votes || {}),
          myVote: event.myVote ?? null,
          resolvedMap: null,
          tally: null,
          teamStats: deepClonePlain(event.teamStats || null),
        },
      };
    case "map-vote-update":
      return {
        ...snapshot,
        lastMessage: event,
        mapVote:
          snapshot.mapVote?.matchId === event.matchId
            ? {
                ...snapshot.mapVote,
                candidates: Array.isArray(event.candidates)
                  ? event.candidates.slice()
                  : snapshot.mapVote.candidates,
                blockedMaps: deepClonePlain(
                  event.blockedMaps || snapshot.mapVote.blockedMaps || []
                ),
                expiresAt: event.expiresAt ?? snapshot.mapVote.expiresAt,
                durationSeconds:
                  event.durationSeconds ?? snapshot.mapVote.durationSeconds,
                voters: deepClonePlain(event.voters || snapshot.mapVote.voters || []),
                votes: deepClonePlain(event.votes || snapshot.mapVote.votes || {}),
                myVote:
                  event.myVote !== undefined
                    ? event.myVote
                    : snapshot.mapVote.myVote,
                teamStats: deepClonePlain(
                  event.teamStats || snapshot.mapVote.teamStats || null
                ),
              }
            : snapshot.mapVote,
      };
    case "map-vote-resolved":
      return {
        ...snapshot,
        lastMessage: event,
        mapVote:
          snapshot.mapVote?.matchId === event.matchId
            ? {
                ...snapshot.mapVote,
                votes: deepClonePlain(event.votes || snapshot.mapVote.votes || {}),
                voters: deepClonePlain(event.voters || snapshot.mapVote.voters || []),
                myVote:
                  event.myVote !== undefined
                    ? event.myVote
                    : snapshot.mapVote.myVote,
                resolvedMap: event.map || null,
                tally: event.tally || {},
                wasTie: Boolean(event.wasTie),
                tieBrokenBy: event.tieBrokenBy || null,
                tiedMaps: Array.isArray(event.tiedMaps) ? event.tiedMaps : [],
                resolvedBy: event.resolvedBy || null,
                topCount: Number.isFinite(Number(event.topCount))
                  ? Number(event.topCount)
                  : null,
                teamStats: deepClonePlain(
                  event.teamStats || snapshot.mapVote.teamStats || null
                ),
              }
            : snapshot.mapVote,
      };
    case "match-server-ready":
      return {
        ...snapshot,
        lastMessage: event,
        matchReady: {
          matchId: event.matchId,
          redirectTo: event.redirectTo,
        },
        // A ready match has already finished voting; leftover mapVote would revive the modal after tab takeover.
        mapVote: null,
      };
    case "queue-status-update":
      return {
        ...snapshot,
        lastMessage: event,
        matchmaking: {
          playersInQueue: event.playersInQueue,
          playersNeeded: event.playersNeeded ?? null,
          estimatedWaitSeconds: event.estimatedWaitSeconds ?? null,
          geoQueue: event.geoQueue ?? null,
          smallerMatchOffer: event.smallerMatchOffer ?? null,
        },
      };
    default:
      return {
        ...snapshot,
        lastMessage: event,
      };
  }
}

export function createRealtimeMirrorStore(initialSnapshot = DEFAULT_REALTIME_SNAPSHOT) {
  let snapshot = cloneRealtimeSnapshot(initialSnapshot);
  const listeners = new Set();

  const publish = () => {
    listeners.forEach((listener) => listener(cloneRealtimeSnapshot(snapshot)));
  };

  return {
    getSnapshot() {
      return cloneRealtimeSnapshot(snapshot);
    },
    replaceSnapshot(nextSnapshot) {
      snapshot = cloneRealtimeSnapshot(nextSnapshot);
      publish();
    },
    applyEvent(event) {
      snapshot = cloneRealtimeSnapshot(reduceRealtimeSnapshot(snapshot, event));
      publish();
      return cloneRealtimeSnapshot(snapshot);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
